import {
  Box,
  Button,
  Checkbox,
  CloseButton,
  Dialog,
  Portal,
  useMediaQuery,
  VStack,
} from "@chakra-ui/react";
import styled from "@emotion/styled";
import { IContestLegalAgreement } from "@/models/contestDetail";
import { breakpoints } from "@/theme/breakpoints";
import { useLocalHook } from "./useLocalHook";

type Props = {
  isOpen: boolean;
  legalAgreement: IContestLegalAgreement;
  onPressSubmit?: () => void;
  onClose?: () => void;
};

export const LegalAgreementModal = ({
  isOpen,
  legalAgreement,
  onPressSubmit,
  onClose,
}: Props) => {
  const [isLargerThanMd] = useMediaQuery([`(min-width: ${breakpoints.md})`], {
    ssr: false,
  });
  const { disabled, isAgreementChecked, handleOnCheckAgreement } =
    useLocalHook();

  return (
    <Dialog.Root
      size={isLargerThanMd ? "lg" : undefined}
      motionPreset="slide-in-bottom"
      placement="center"
      open={isOpen}
      onOpenChange={(e) => {
        if (!e.open) {
          onClose?.();
        }
      }}
    >
      <Portal>
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content
            h="full"
            maxH={{
              base: "100svh",
              md: "60vh",
            }}
            bg="backgroundDialog"
            borderRadius={{
              base: 0,
              md: 8,
            }}
          >
            <Dialog.Header justifyContent="center">
              <Dialog.Title
                color="contentPrimary"
                fontSize={20}
                fontWeight="bold"
              >
                コンテスト応募規約
              </Dialog.Title>
            </Dialog.Header>
            <Dialog.Body pt="0" w="100%" h="100%" minH={0}>
              <Box
                p="4"
                bg="backgroundPrimary"
                w="full"
                h="full"
                borderRadius={8}
                borderWidth={1}
                borderColor="borderBorderPrimary"
                overflowY="auto"
                scrollbarWidth="none"
              >
                <StyledHTMLContainer
                  dangerouslySetInnerHTML={{
                    __html: legalAgreement.latestVersion.descriptionHtml,
                  }}
                />
              </Box>
            </Dialog.Body>
            <Dialog.Footer w="full" justifyContent="center" flexShrink={0}>
              <VStack w="full" gap="4" alignItems="center">
                <Checkbox.Root
                  checked={isAgreementChecked}
                  onCheckedChange={handleOnCheckAgreement}
                >
                  <Checkbox.HiddenInput />
                  <Checkbox.Control
                    _checked={{
                      bg: "backgroundLinkPrimary",
                      color: "white",
                      borderColor: "transparent",
                    }}
                    borderColor="#989BA1"
                    borderWidth={2}
                    borderRadius={4}
                  />
                  <Checkbox.Label color="contentPrimary" fontSize={14}>
                    規約を確認、理解した上で同意します
                  </Checkbox.Label>
                </Checkbox.Root>

                <Button
                  color="white"
                  bg="backgroundLinkPrimary"
                  borderRadius="full"
                  fontSize={16}
                  fontWeight="bold"
                  w="full"
                  maxW="420px"
                  h="48px"
                  _disabled={{
                    bg: "backgroundDisable",
                  }}
                  disabled={disabled}
                  onClick={onPressSubmit}
                >
                  コンテストに参加する
                </Button>
              </VStack>
            </Dialog.Footer>
            <Dialog.CloseTrigger
              asChild
              top={{
                base: "4",
                md: "2",
              }}
              left={{
                base: "0",
                md: "unset",
              }}
              right={{
                base: "unset",
                md: "2",
              }}
              insetEnd="unset"
            >
              <CloseButton size="md" color="contentPrimary" />
            </Dialog.CloseTrigger>
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  );
};

const StyledHTMLContainer = styled(Box)`
  color: #181e26;
  font-size: 14px;
  line-height: 1.5;

  ul > li {
    line-height: 1.8;
  }
  h1 {
    font-size: 24px;
    line-height: 1.7;
    margin-bottom: 16px;
  }
  h2,
  h3,
  h4 {
    margin-bottom: 16px;
  }
  p {
    margin-bottom: 16px;
  }
  img {
    max-width: 100%;
    height: auto;
    margin-top: 16px;
    margin-bottom: 16px;
  }
  blockquote {
    max-width: 500px;
    padding: 1em 1.5em;
    border-left: 4px solid #d6dde3;
    color: #333;
  }
  blockquote:has(cite) {
    padding-bottom: 0.5em;
  }
  blockquote p {
    margin-top: 0;
  }
  blockquote cite {
    display: block;
    color: #737373;
    font-size: 0.8em;
    text-align: right;
  }
`;
